<template>
  <div class="page">
    <a-row style="margin: 10px 0">
      <a-col :span="24">
        <!-- ✅ 使用 FormModel，并挂 rules -->
        <a-form-model ref="formRef" :model="form" :rules="rules">
          <!-- 标题 -->
          <div class="title">更新登录密码</div>

          <!-- 用户名 -->
          <div class="info-row">
            <span class="label">手机号：</span>
            <span class="mobile">{{ mobile }}</span>
          </div>

          <!-- 密码 -->
          <a-form-model-item prop="password" style="margin-bottom: 10px">
            <a-input-password v-model="form.password" placeholder="请输入密码">
              <a-icon
                slot="prefix"
                type="lock"
                :style="{ color: 'rgba(0,0,0,.25)' }"
              />
            </a-input-password>
          </a-form-model-item>

          <!-- 确认密码 -->
          <a-form-model-item prop="confirmPassword" style="margin-bottom: 10px">
            <a-input-password
              v-model="form.confirmPassword"
              placeholder="请再次输入密码"
            >
              <a-icon
                slot="prefix"
                type="lock"
                :style="{ color: 'rgba(0,0,0,.25)' }"
              />
            </a-input-password>
          </a-form-model-item>

          <!-- 验证码 -->
          <a-row :gutter="15">
            <a-col :span="15">
              <a-form-model-item prop="captcha">
                <a-input v-model="form.captcha" placeholder="请输入验证码">
                  <a-icon
                    slot="prefix"
                    type="mail"
                    :style="{ color: 'rgba(0,0,0,.25)' }"
                  />
                </a-input>
              </a-form-model-item>
            </a-col>

            <a-col :span="9">
              <a-button
                class="getCaptcha"
                :disabled="state.smsSendBtn || state.captchaLoading"
                :loading="state.captchaLoading"
                @click.stop.prevent="getCaptcha"
              >
                {{
                  !state.smsSendBtn ? "获取验证码" : `${state.time}秒后重新发送`
                }}
              </a-button>
            </a-col>
          </a-row>

          <!-- 阿里云验证码容器 -->
          <div
            id="captcha-register"
            :class="captchaConfig.config.product === 'bind' ? 'hideHeight' : ''"
          >
            <AliCaptcha :captcha-config="captchaConfig" />
          </div>

          <!-- 验证码提示 -->
          <p
            v-if="state.showCaptchaMsg"
            class="captcha-tip-item"
            :style="{
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              marginTop: '4px',
            }"
          >
            <a-icon
              :type="tipIconType"
              :style="{
                color: tipIconColor,
                marginRight: '6px',
                verticalAlign: 'middle',
              }"
            />
            <span style="line-height: 1">{{ showCaptchaMsg }}</span>
          </p>

          <!-- 注册按钮（仅点击触发） -->
          <a-form-model-item>
            <a-button
              style="width: 100%"
              size="large"
              type="primary"
              class="register-button"
              :loading="state.registerBtn"
              :disabled="state.registerBtn"
              @click="handleRegister"
            >
              更新
            </a-button>
          </a-form-model-item>
        </a-form-model>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import AliCaptcha from "@/components/AliCaptcha.vue";
import {
  photoCaptchaSendSms,
} from "@/api/user/register/Register.js";
import {
  updatePassword,
} from "@/api/user/personalCenter/PersonalCenter.js";

export default {
  props: {
    mobile: {
      type: String,
      default: () => null,
    },
  },
  components: { AliCaptcha },
  data() {
    return {
      // ✅ 用于 v-model 的表单数据
      form: {
        password: "",
        confirmPassword: "",
        captcha: "",
      },
      // ✅ 统一的 rules（触发你的原有方法）
      rules: {
        password: [
          {
            validator: (rule, value, callback) =>
              this.validatePasswordComplexity(rule, value, callback),
            trigger: "blur",
          },
        ],
        confirmPassword: [
          { required: true, message: "请确认密码！", trigger: "blur" },
          {
            validator: (rule, value, callback) =>
              this.validateConfirmPassword(rule, value, callback),
            trigger: "blur",
          },
        ],
        captcha: [{ required: true, message: "请输入验证码", trigger: "blur" }],
      },
      state: {
        time: 60,
        registerBtn: false,
        smsSendBtn: false,
        showCaptchaMsg: false,
        captchaStatus: "success",
        captchaLoading: false, // ✅ 新增：验证码按钮loading
      },
      showCaptchaMsg: "短信验证码已发送，可能会有延迟，请耐心等待。",
      captchaObj: null,
      captchaConfig: {
        config: {
          captchaId: "e63c338c75217f3dfaccad44160f8369",
          language: "cn",
          product: "bind",
        },
        handler: this.captchaHandler,
      },
    };
  },
  computed: {
    // 当前提示使用的图标类型
    tipIconType() {
      const map = {
        success: "check-circle",
        warning: "exclamation-circle",
        error: "close-circle",
      };
      return map[this.state.captchaStatus] || map.success;
    },

    // 图标颜色：success=绿色，warning=黄色，error=红色
    tipIconColor() {
      const map = {
        success: "#52c41a", // 绿色
        warning: "#faad14", // 黄色
        error: "#f5222d", // 红色
      };
      return map[this.state.captchaStatus] || map.success;
    },
  },
  methods: {
    /** ✅ 密码复杂度校验 */
    validatePasswordComplexity(rule, value, callback) {
      if (!value) return callback("请输入密码！");

      // 1️⃣ 长度校验
      if (value.length < 8 || value.length > 20) {
        return callback("密码长度需为 8-20 位！");
      }

      // 2️⃣ 必须包含大写、小写、数字、特殊符号
      const upper = /[A-Z]/;
      const lower = /[a-z]/;
      const digit = /[0-9]/;
      const special =
        /[~!@#$%^&*()_\-+=<>?{}[\]|:;"',./`￥！……（）—【】、；‘’，。《》？]/;

      if (
        !upper.test(value) ||
        !lower.test(value) ||
        !digit.test(value) ||
        !special.test(value)
      ) {
        return callback("密码需包含大写字母、小写字母、数字和特殊符号！");
      }

      // 3️⃣ 不得包含手机号或用户名
      const mobile = this.mobile || "";
      if (mobile && value.includes(mobile)) {
        return callback("密码不能包含手机号！");
      }
      return callback();
    },

    /** ✅ 确认密码一致性（原逻辑保留，rules 中调用它） */
    validateConfirmPassword(rule, value, callback) {
      if (value && value !== this.form.password) {
        callback("两次输入的密码不一致！");
      } else {
        callback();
      }
    },

    /** 获取验证码（原逻辑保留） */
    getCaptcha() {
      if (this.captchaConfig.config.product === "bind") {
        if (this.captchaObj) {
          this.captchaObj.showCaptcha();
        } else {
          this.$notification.warning({
            message: `登录图形验证框加载中`,
            description: `请稍后`,
          });
          return false;
        }
      } else {
        this.validate();
      }
    },

    /** 倒计时（原逻辑保留） */
    countDown() {
      if (this.state.smsSendBtn) return;
      this.state.smsSendBtn = true;
      this.state.time = 60;
      this.state.showCaptchaMsg = true;

      const timer = setInterval(() => {
        this.state.time--;
        if (this.state.time <= 0) {
          clearInterval(timer);
          this.state.smsSendBtn = false;
          this.state.showCaptchaMsg = false;
        }
      }, 1000);
    },

    //阿里云图形验证注册发送短信
    validate() {
      this.state.captchaLoading = true;
      const _this = this;
      const result = this.captchaObj.getValidate();

      if (!result) {
        _this.$message.warning("请先完成图形验证！");
        _this.state.captchaLoading = false;
        return;
      }
      photoCaptchaSendSms({
        lotNumber: result.lot_number,
        captchaOutput: result.captcha_output,
        passToken: result.pass_token,
        genTime: result.gen_time,
        phoneNumbers: _this.mobile, // 建议改为 this.form.mobile
      })
        .then((res) => {
          if (res && res.code === "200") {
            _this.state.captchaStatus = "success";
            _this.showCaptchaMsg =
              "短信验证码已发送，可能会有延迟，请耐心等待。";
            _this.state.showCaptchaMsg = true;
            _this.state.captchaLoading = false;
            _this.countDown();
          } else {
            _this.showCaptchaMsg = (res && res.msg) || "验证码发送失败，请重试";
            _this.state.captchaStatus = "warning";
            _this.state.showCaptchaMsg = true;
            _this.state.captchaLoading = false;
          }
        })
        .catch((err) => {
          console.error("接口调用异常：", err);
          _this.state.captchaStatus = "warning";
          _this.showCaptchaMsg = "网络异常，请稍后重试！";
          _this.state.showCaptchaMsg = true;
          _this.state.captchaLoading = false;
        });
    },

    /** 阿里验证码回调（原逻辑保留） */
    captchaHandler(captchaObj) {
      this.captchaObj = captchaObj;
      captchaObj
        .appendTo("#captcha-register")
        .onReady(function () {})
        .onNextReady(function () {})
        .onBoxShow(function () {})
        .onError(function (e) {})
        .onSuccess(() => {
          if (this.captchaConfig.config.product === "bind") {
            this.validate();
          }
        });
    },

    /** 注册提交：点击按钮触发（仅点击，不走回车提交） */
    handleRegister() {
      // 先整体验证（使用 rules）
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;

        this.state.registerBtn = true;
        updatePassword({
          mobile: this.mobile,
          password: this.form.password,
          captcha: this.form.captcha,
          confirmPassword: this.form.confirmPassword,
        })
          .then((res) => {
            if (res && res.code === "200") {
              setTimeout(() => {
                this.$notification.success({
                  message: `更新密码`,
                  description: `成功`,
                });
                this.state.registerBtn = false;
                this.form = {
                  password: "",
                  confirmPassword: "",
                  captcha: "",
                };
                this.state.showCaptchaMsg = false;
                this.$refs.formRef.resetFields();
                this.$emit("closePasswordMoudle");
              }, 1000);
            } else {
              setTimeout(() => {
                this.$notification.error({
                  message: `更新失败`,
                  description: res.msg,
                });
                this.state.registerBtn = false;
              }, 1000);
            }
          })
          .catch((err) => {
            this.state.registerBtn = false;
          });
      });
    },
  },
};
</script>

<style scoped>
.page {
  max-width: 420px;
  margin: 0 auto;
}

.title {
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  color: #333;
  letter-spacing: 1px;
  margin-bottom: 24px;
  position: relative;
}

.getCaptcha {
  display: block;
  width: 100%;
  height: 50px;
  border-radius: 4px;
  font-weight: 500;
}

.register-button {
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 1px;
}

.captcha-tip-item {
  margin-top: -5px;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
}

/* 用户名行 */
.info-row {
  margin: 12px 0;
  display: flex;
  align-items: center;
}

.label {
  font-weight: 600;
  font-size: 15px;
  color: #333;
  line-height: 32px;
}

.mobile{
  font-weight: 600;
  font-size: 15px;
  color: #333;
  line-height: 32px;
}
</style>
